// Delivery Date Dashboard: detail modal, Statistics tab, toasts and Excel export headers.
export default {
  en: {
    deliveryModal: {
      stats: {
        usageTitle: 'Tool usage',
        usageDesc: 'Who changes promised delivery dates through this dashboard vs. directly in JTL, and how that develops over time.',
        window: 'Window',
        weeksOption: '{n} weeks',
        since: 'Since {date} · promised-date changes on customer orders; "Automatic" = JTL import workflows, excluded from the tool share.',
        viaToolTitle: 'Date changes via tool',
        viaToolHint: 'manual + auto-applied changes made through this dashboard.',
        directTitle: 'Directly in JTL',
        directHint: 'a person changed the date directly in JTL, bypassing the tool.',
        lastWeeks: 'Last {n} weeks',
        shareTitle: 'Tool share',
        overall: 'Overall',
        shareHint: '(tool + auto-applied) ÷ (tool + auto + JTL direct + system/other).',
        byWeekTitle: 'Usage by week',
        byWeekDesc: 'Tool vs. direct-JTL date changes, and the tool\'s share of the total',
        byUserTitle: 'Usage by user',
        byUserDesc: 'Sorted by total date changes — rows in red changed dates in JTL but did nothing in the tool (no date change, comment, note, priority, …)',
        noData: 'No usage data for this window',
        changes: 'Changes',
        series: {
          tool: 'Via tool',
          auto: 'Auto-applied',
          jtl: 'Directly in JTL',
          system: 'System/Worker',
          share: 'Tool share %',
        },
        actions: {
          comment: 'Comments',
          partial_delivery: 'Partial-delivery notes',
          priority: 'Priority changes',
          notes: 'Order notes saved',
          supplier_order_date: 'Supplier order dates',
          supplier_order_notes: 'Supplier order notes',
          suggestion_reject: 'Suggestions rejected',
          suggestion_unreject: 'Rejections undone',
        },
      },
      detail: {
        titleOrder: 'Order {no}',
        titleLb: 'LB {no}',
        customer: 'Customer',
        source: 'Source',
        holdBadge: 'On hold: {reason}',
        owner: 'Owner',
        address: 'Address',
        email: 'Email',
        phone: 'Phone',
        orderDate: 'Order date',
        lbOrderDate: 'Order date',
        promisedDelivery: 'Promised delivery',
        deliveryDate: 'Delivery date',
        noDatePlaceholder: 'No date',
        reasonPlaceholder: 'Reason for change…',
        notePlaceholder: 'Note (optional)',
        chooseReason: 'Choose a reason to save a manual change',
        suggestedLine: 'Suggested {date} ({basis})',
        basisStock: 'from stock',
        basisSupplier: 'supplier date + {days}d',
        awaitingConfirmation: 'Ordered, awaiting supplier confirmation{lb}',
        supplierMissed: 'Supplier date missed{lb} — chase purchasing',
        needsPurchasing: 'Needs purchasing — nothing ordered',
        warnLate: '⚠ Supplier delivery {incoming} is after the promised date {promised}',
        warnOverdue: '⚠ A supplier arrival this order waits on is already overdue — goods not arrived',
        applyNewDate: 'Apply new date {date}',
        payment: 'Payment',
        paidOn: 'Paid on {date}',
        orderedOn: 'Ordered on {date}',
        quickFromPayment: 'Counted from payment date {date}',
        shipping: 'Shipping',
        externalOrderNo: 'External order no.',
        priority: 'Priority',
        saveOthersFirst: 'Save or discard your other changes first',
        orderTotal: 'Order total (net)',
        lbOrderTotal: 'Order total (net)',
        customerNote: 'Customer note',
        noCustomerNote: 'No customer note',
        internalComment: 'Internal comment',
        noInternalComment: 'No internal comment',
        addCommentPlaceholder: 'Add comment (stamped with date and your initials)',
        saveCommentFirst: 'Save the internal comment edits first to add a comment.',
        saveNotes: 'Save notes',
        partialPossible: 'Partial delivery possible',
        partialStock: '{available} of {total} positions in stock now, remainder est. {date}.',
        noteInComment: 'Note in internal comment',
        positionsCount: 'Positions ({n})',
        noPositions: 'No positions',
        linkedLbs: 'Linked supplier orders ({n})',
        noLinkedLbs: 'No linked supplier orders',
        availableQty: '{n} available',
        reserved: 'Reserved for this order',
        outOfStock: 'Out of stock',
        matchArticle: 'Article',
        matchOrderRef: 'Order ref',
        supplier: 'Supplier',
        buyer: 'Buyer',
        status: 'Status',
        reference: 'Reference',
      },
      deliveryTime: {
        atOrder: 'at order',
        unchanged: 'unchanged',
        unchangedSince: 'unchanged since {date}',
        current: 'current · article edited',
        currentSince: 'current · article edited {date}',
        tipSnapshot: 'This is the delivery time we showed the customer at the moment they placed the order.',
        tipUnchanged: 'The article has not been edited since the order came in, so the current delivery time is certainly the one shown to the customer at order time.',
        tipMaybeChanged: 'The article was edited after the order came in, the delivery time may have been different at order time.',
      },
      rejectModal: {
        title: 'Reject suggested date {no}',
        rejecting: 'Rejecting…',
        reason: 'Reason',
        reasonPlaceholder: 'Reason for rejecting…',
        note: 'Note (optional)',
      },
      exp: {
        incoming: 'Incoming',
        partial: 'Partial delivery',
        paidDate: 'Paid on',
        posWithoutDate: 'Pos. without date',
        blocksLateOrder: 'Blocks late order',
        lateSeverity: 'Late severity',
        definitelyLate: 'definitely late',
        overdue: 'overdue',
        supplierOverdue: 'supplier overdue',
        autoApply: 'Auto-apply',
        pending: 'Pending until {date}',
        skipped: 'Skipped: {reason}',
        rejected: 'Rejected: {reason}',
        onHold: 'On hold',
      },
      toast: {
        copied: '{text} copied',
        usageWeekError: 'Error loading usage by week',
        usageUserError: 'Error loading usage by user',
        lbDateSaved: 'Delivery date saved to JTL',
        lbDateError: 'Error saving delivery date',
        notesSaved: 'Notes saved to JTL',
        notesError: 'Error saving notes',
        promisedSaved: 'Promised delivery saved to JTL',
        chooseReasonChange: 'Choose a reason for the change',
        promisedError: 'Error saving promised delivery',
        prioritySaved: 'Priority saved to JTL',
        priorityError: 'Error saving priority',
        queueError: 'Error loading auto-apply review list',
        suggestionRejected: 'Suggestion rejected for {no}',
        chooseReason: 'Choose a reason',
        rejectError: 'Error rejecting suggestion',
        rejectionUndone: 'Rejection undone for {no}',
        undoError: 'Error undoing rejection',
        promisedSavedFor: 'Promised delivery {date} saved for {no}',
        chooseReasonThenSave: 'Choose a reason, then Save',
        noteAppended: 'Note appended to internal comment',
        noteError: 'Error appending note',
        commentAdded: 'Comment added to JTL',
        commentReloadFailed: 'Comment added, but reloading it failed',
        commentError: 'Error adding comment',
      },
    },
  },
  de: {
    deliveryModal: {
      stats: {
        usageTitle: 'Tool-Nutzung',
        usageDesc: 'Wer zugesagte Lieferdaten über dieses Dashboard ändert und wer direkt in JTL, und wie sich das entwickelt.',
        window: 'Zeitraum',
        weeksOption: '{n} Wochen',
        since: 'Seit {date} · Änderungen des zugesagten Lieferdatums bei Kundenaufträgen; „Automatisch“ = JTL-Import-Workflows, nicht im Tool-Anteil enthalten.',
        viaToolTitle: 'Datumsänderungen über Tool',
        viaToolHint: 'manuelle + automatisch übernommene Änderungen über dieses Dashboard.',
        directTitle: 'Direkt in JTL',
        directHint: 'eine Person hat das Datum direkt in JTL geändert, am Tool vorbei.',
        lastWeeks: 'Letzte {n} Wochen',
        shareTitle: 'Tool-Anteil',
        overall: 'Gesamt',
        shareHint: '(Tool + automatisch) ÷ (Tool + automatisch + direkt in JTL + System/Sonstige).',
        byWeekTitle: 'Nutzung pro Woche',
        byWeekDesc: 'Datumsänderungen über Tool vs. direkt in JTL und der Tool-Anteil am Gesamt',
        byUserTitle: 'Nutzung pro Benutzer',
        byUserDesc: 'Sortiert nach Datumsänderungen gesamt — rot markierte Zeilen haben Daten in JTL geändert, aber im Tool nichts getan (keine Datumsänderung, Kommentar, Notiz, Priorität, …)',
        noData: 'Keine Nutzungsdaten für diesen Zeitraum',
        changes: 'Änderungen',
        series: {
          tool: 'Über Tool',
          auto: 'Automatisch übernommen',
          jtl: 'Direkt in JTL',
          system: 'System/Worker',
          share: 'Tool-Anteil %',
        },
        actions: {
          comment: 'Kommentare',
          partial_delivery: 'Teillieferungs-Notizen',
          priority: 'Prioritätsänderungen',
          notes: 'Auftragsnotizen gespeichert',
          supplier_order_date: 'LB-Liefertermine',
          supplier_order_notes: 'LB-Notizen',
          suggestion_reject: 'Vorschläge abgelehnt',
          suggestion_unreject: 'Ablehnungen zurückgenommen',
        },
      },
      detail: {
        titleOrder: 'Auftrag {no}',
        titleLb: 'LB {no}',
        customer: 'Kunde',
        source: 'Quelle',
        holdBadge: 'Zurückgehalten: {reason}',
        owner: 'Zuständig',
        address: 'Adresse',
        email: 'E-Mail',
        phone: 'Telefon',
        orderDate: 'Auftragsdatum',
        lbOrderDate: 'Bestelldatum',
        promisedDelivery: 'Zugesagtes Lieferdatum',
        deliveryDate: 'Lieferdatum',
        noDatePlaceholder: 'Kein Datum',
        reasonPlaceholder: 'Grund der Änderung…',
        notePlaceholder: 'Notiz (optional)',
        chooseReason: 'Für eine manuelle Änderung bitte einen Grund wählen',
        suggestedLine: 'Vorschlag {date} ({basis})',
        basisStock: 'ab Lager',
        basisSupplier: 'Lieferantentermin + {days} T.',
        awaitingConfirmation: 'Bestellt, Bestätigung des Lieferanten ausstehend{lb}',
        supplierMissed: 'Lieferantentermin verpasst{lb} — beim Einkauf nachfassen',
        needsPurchasing: 'Einkauf nötig — nichts bestellt',
        warnLate: '⚠ Zulauf {incoming} liegt nach dem zugesagten Lieferdatum {promised}',
        warnOverdue: '⚠ Ein Zulauf, auf den dieser Auftrag wartet, ist überfällig — Ware nicht eingegangen',
        applyNewDate: 'Neues Datum {date} übernehmen',
        payment: 'Zahlungsart',
        paidOn: 'Bezahlt am {date}',
        orderedOn: 'Bestellt am {date}',
        quickFromPayment: 'Gerechnet ab Zahlungseingang {date}',
        shipping: 'Versandart',
        externalOrderNo: 'Externe Auftragsnr.',
        priority: 'Priorität',
        saveOthersFirst: 'Bitte zuerst die anderen Änderungen speichern oder verwerfen',
        orderTotal: 'Auftragswert (netto)',
        lbOrderTotal: 'Bestellwert (netto)',
        customerNote: 'Kundenanmerkung',
        noCustomerNote: 'Keine Kundenanmerkung',
        internalComment: 'Interner Kommentar',
        noInternalComment: 'Kein interner Kommentar',
        addCommentPlaceholder: 'Kommentar hinzufügen (mit Datum und Kürzel versehen)',
        saveCommentFirst: 'Zum Hinzufügen eines Kommentars zuerst die Änderungen am internen Kommentar speichern.',
        saveNotes: 'Notizen speichern',
        partialPossible: 'Teillieferung möglich',
        partialStock: '{available} von {total} Positionen jetzt auf Lager, Rest vsl. {date}.',
        noteInComment: 'Im internen Kommentar vermerken',
        positionsCount: 'Positionen ({n})',
        noPositions: 'Keine Positionen',
        linkedLbs: 'Verknüpfte Lieferantenbestellungen ({n})',
        noLinkedLbs: 'Keine verknüpften Lieferantenbestellungen',
        availableQty: '{n} verfügbar',
        reserved: 'Für diesen Auftrag reserviert',
        outOfStock: 'Nicht auf Lager',
        matchArticle: 'Artikel',
        matchOrderRef: 'Auftragsbezug',
        supplier: 'Lieferant',
        buyer: 'Einkäufer',
        status: 'Status',
        reference: 'Referenz',
      },
      deliveryTime: {
        atOrder: 'bei Bestellung',
        unchanged: 'unverändert',
        unchangedSince: 'unverändert seit {date}',
        current: 'aktuell · Artikel geändert',
        currentSince: 'aktuell · Artikel geändert {date}',
        tipSnapshot: 'Das ist die Lieferzeit, die dem Kunden beim Bestellen angezeigt wurde.',
        tipUnchanged: 'Der Artikel wurde seit Auftragseingang nicht geändert, die aktuelle Lieferzeit entspricht daher sicher der, die dem Kunden bei der Bestellung angezeigt wurde.',
        tipMaybeChanged: 'Der Artikel wurde nach Auftragseingang geändert, die Lieferzeit kann bei der Bestellung eine andere gewesen sein.',
      },
      rejectModal: {
        title: 'Datumsvorschlag {no} ablehnen',
        rejecting: 'Wird abgelehnt…',
        reason: 'Grund',
        reasonPlaceholder: 'Grund der Ablehnung…',
        note: 'Notiz (optional)',
      },
      exp: {
        incoming: 'Zulauf',
        partial: 'Teillieferung',
        paidDate: 'Bezahlt am',
        posWithoutDate: 'Pos. ohne Datum',
        blocksLateOrder: 'Blockiert verspäteten Auftrag',
        lateSeverity: 'Verspätungsgrad',
        definitelyLate: 'sicher verspätet',
        overdue: 'überfällig',
        supplierOverdue: 'Lieferant überfällig',
        autoApply: 'Auto-Übernahme',
        pending: 'Ausstehend bis {date}',
        skipped: 'Übersprungen: {reason}',
        rejected: 'Abgelehnt: {reason}',
        onHold: 'Zurückgehalten',
      },
      toast: {
        copied: '{text} kopiert',
        usageWeekError: 'Fehler beim Laden der Nutzung pro Woche',
        usageUserError: 'Fehler beim Laden der Nutzung pro Benutzer',
        lbDateSaved: 'Lieferdatum in JTL gespeichert',
        lbDateError: 'Fehler beim Speichern des Lieferdatums',
        notesSaved: 'Notizen in JTL gespeichert',
        notesError: 'Fehler beim Speichern der Notizen',
        promisedSaved: 'Zugesagtes Lieferdatum in JTL gespeichert',
        chooseReasonChange: 'Bitte einen Grund für die Änderung wählen',
        promisedError: 'Fehler beim Speichern des zugesagten Lieferdatums',
        prioritySaved: 'Priorität in JTL gespeichert',
        priorityError: 'Fehler beim Speichern der Priorität',
        queueError: 'Fehler beim Laden der Prüfliste der Auto-Übernahme',
        suggestionRejected: 'Vorschlag für {no} abgelehnt',
        chooseReason: 'Bitte einen Grund wählen',
        rejectError: 'Fehler beim Ablehnen des Vorschlags',
        rejectionUndone: 'Ablehnung für {no} zurückgenommen',
        undoError: 'Fehler beim Zurücknehmen der Ablehnung',
        promisedSavedFor: 'Zugesagtes Lieferdatum {date} für {no} gespeichert',
        chooseReasonThenSave: 'Bitte einen Grund wählen, dann speichern',
        noteAppended: 'Vermerk an internen Kommentar angehängt',
        noteError: 'Fehler beim Anhängen des Vermerks',
        commentAdded: 'Kommentar in JTL hinzugefügt',
        commentReloadFailed: 'Kommentar hinzugefügt, aber das Neuladen ist fehlgeschlagen',
        commentError: 'Fehler beim Hinzufügen des Kommentars',
      },
    },
  },
}
